/**
 * Utility to extract YouTube Video ID from various URL formats
 */
export const getYouTubeId = (url: string): string | null => {
    if (!url) return null;

    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
    const match = url.match(regExp);

    return (match && match[2].length === 11) ? match[2] : null;
};

/**
 * Checks if a string is a YouTube URL
 */
export const isYouTubeUrl = (url: string): boolean => {
    return !!getYouTubeId(url);
};

/**
 * Generates a YouTube embed URL with safety parameters
 */
export const getYouTubeEmbedUrl = (url: string): string | null => {
    const id = getYouTubeId(url);
    if (!id) return null;

    // Using youtube-nocookie.com for better privacy and performance
    return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&autoplay=0`;
};

/**
 * Utility to extract Vimeo Video ID from various URL formats
 */
export const getVimeoId = (url: string): string | null => {
    if (!url) return null;

    const regExp = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const match = url.match(regExp);

    return (match && match[3]) ? match[3] : null;
};

/**
 * Generates a Vimeo embed URL
 */
export const getVimeoEmbedUrl = (url: string): string | null => {
    const id = getVimeoId(url);
    if (!id) return null;

    return `https://player.vimeo.com/video/${id}?badge=0&autopause=0&player_id=0&app_id=58479`;
};

/**
 * Utility to extract TikTok Video ID
 */
export const getTikTokId = (url: string): string | null => {
    if (!url) return null;
    const match = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
    return match ? match[1] : null;
};

export const getTikTokEmbedUrl = (url: string): string | null => {
    const id = getTikTokId(url);
    return id ? `https://www.tiktok.com/embed/v2/${id}` : null;
};

/**
 * Utility to extract Instagram ID
 */
export const getInstagramId = (url: string): string | null => {
    if (!url) return null;
    const match = url.match(/instagram\.com\/(?:p|reels|tv)\/([\w-]+)/);
    return match ? match[1] : null;
};

export const getInstagramEmbedUrl = (url: string): string | null => {
    const id = getInstagramId(url);
    if (!id) return null;

    // Normalize IG Reels/Posts to a consistent embed format
    return `https://www.instagram.com/p/${id}/embed`;
};

/**
 * Utility to extract Facebook Video ID or validate URL
 */
export const getFacebookId = (url: string): string | null => {
    if (!url) return null;
    // Catch numeric or alphanumeric IDs in various paths including web.facebook.com
    const idMatch = url.match(/(?:www\.|web\.)?facebook\.com\/(?:watch\/\?v=|videos\/|reels?\/|[\w.]+\/videos\/|share\/r\/)([\w-]+)/);
    if (idMatch) return idMatch[1];

    // Check if it's a general FB video/reel/watch URL
    if (url.match(/(?:www\.|web\.)?facebook\.com/) && (url.includes('/videos/') || url.includes('/watch') || url.includes('/share/') || url.includes('/reels'))) {
        return 'valid_url';
    }
    return null;
};

export const getFacebookEmbedUrl = (url: string): string | null => {
    if (!url) return null;

    const id = getFacebookId(url);
    if (!id) return null;

    // Force https and www.facebook.com for the plugin source
    let finalUrl = url.replace(/^(?:https?:\/\/)?(?:web\.|m\.)?facebook\.com/, 'https://www.facebook.com');

    // RUTHLESS FIX REFINED: Use the /watch/?v=ID format which is often more stable for the OEmbed plugin
    // extracted alphanumeric ID from share/r/ or reel/ or videos/
    const idMatch = finalUrl.match(/(?:reels?|share\/(?:r|v)|videos|watch\/\?v=)\/([a-zA-Z0-9_-]+)/) || finalUrl.match(/v=([a-zA-Z0-9_-]+)/);

    if (idMatch) {
        // Force a stable watch URL format for the OEmbed plugin
        finalUrl = `https://www.facebook.com/watch/?v=${idMatch[1]}`;
    }

    // Use the normalized/final URL for the plugin
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(finalUrl)}&show_text=0&width=500&t=0`;
};

/**
 * Utility to extract X (Twitter) ID
 */
export const getXId = (url: string): string | null => {
    if (!url) return null;
    const match = url.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
    return match ? match[1] : null;
};

export const getXEmbedUrl = (url: string): string | null => {
    const id = getXId(url);
    // Twitter's official embed iframe endpoint
    return id ? `https://platform.twitter.com/embed/Tweet.html?id=${id}` : null;
};
/**
 * Calculates estimated read time for content in minutes
 */
export const calculateReadTime = (content: string): number => {
    if (!content) return 0;
    // Average reading speed: 200-250 words per minute
    const wordsPerMinute = 225;
    // Remove HTML tags for accurate word count
    const cleanContent = content.replace(/<[^>]*>/g, ' ');
    const words = cleanContent.trim().split(/\s+/).filter(word => word.length > 0).length;
    return Math.max(1, Math.ceil(words / wordsPerMinute));
};
